import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useQuery } from "@tanstack/react-query";
import type {
  DraftField,
  DraftSaveRequest,
  DraftSection,
  DraftValidationIssue,
  FieldTypeSummary,
  LogicRule,
} from "@sga/shared";
import { apiGet, apiFetch, apiPost } from "../lib/api.js";
import { FieldPalette } from "./FieldPalette.jsx";
import { Canvas } from "./Canvas.jsx";
import { PropertiesPanel } from "./PropertiesPanel.jsx";
import { PreviewPane } from "./PreviewPane.jsx";
import type { PreviewWidth } from "@sga/shared/form-renderer";
import { LogicTab } from "./LogicTab.jsx";
import { SettingsTab, DesignTab } from "./SettingsTabs.jsx";
import { DedupeRulesPanel } from "./DedupeRulesPanel.jsx";
import { NotificationsPanel } from "./NotificationsPanel.jsx";
import { WebhooksPanel } from "./WebhooksPanel.jsx";
import { PublishModal, type PublishDiff } from "./PublishModal.jsx";
import { readLocalDraft, clearLocalDraft, useBuilderState } from "./useBuilderState.js";
import { canJoinPreviousRow, joinPreviousRow, splitFromRow } from "./rowGroups.js";

type Tab = "build" | "logic" | "settings" | "design" | "duplicates" | "notifications" | "webhooks";

const EMPTY_SCHEMA: DraftSaveRequest = {
  sections: [],
  fields: [],
  logic: [],
  privacyNotice: "",
  settings: {},
};

/** snake_case key derived from the label, then locked — it is the export column header. */
const deriveKey = (label: string, existing: Set<string>): string => {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(\d)/, "field_$1") || "field";

  if (!existing.has(base)) return base;
  let suffix = 2;
  while (existing.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

const SAVE_STATE_LABEL: Record<string, string> = {
  idle: "All changes saved",
  dirty: "Unsaved changes",
  saving: "Saving…",
  saved: "Saved",
  error: "Could not save — retrying",
};

export const FormBuilder = ({
  formId,
  onExit,
}: {
  formId: string;
  onExit: () => void;
}): JSX.Element => {
  const [tab, setTab] = useState<Tab>("build");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [issues, setIssues] = useState<DraftValidationIssue[]>([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishDiff, setPublishDiff] = useState<PublishDiff | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [recovered, setRecovered] = useState<{ savedAt: number } | null>(null);
  const [previewWidth, setPreviewWidth] = useState<PreviewWidth>("desktop");

  const fieldTypesQuery = useQuery({
    queryKey: ["field-types"],
    queryFn: () => apiGet<{ data: FieldTypeSummary[] }>("/field-types"),
    staleTime: Infinity, // the registry only changes on deploy
  });

  const draftQuery = useQuery({
    queryKey: ["form-draft", formId],
    queryFn: () =>
      apiGet<{
        sections: DraftSection[];
        fields: (DraftField & { sectionKey: string | null })[];
        logic: LogicRule[];
        privacyNotice: string | null;
        settings: Record<string, unknown>;
      }>(`/forms/${formId}/draft`),
  });

  // Just the programme's areas, for the State/LGA preset in the properties panel. The form row is
  // small and already cached by the behaviour panel, so this is not an extra request in practice.
  const formQuery = useQuery({
    queryKey: ["form", formId],
    queryFn: () => apiGet<{ programme?: { areaStates?: string[] } }>(`/forms/${formId}`),
    retry: false,
  });

  const profileAttributesQuery = useQuery({
    queryKey: ["profile-attributes"],
    queryFn: () =>
      apiGet<{ data: { key: string; label: string; category: string }[] }>("/profile-attributes"),
    // Not fatal if it 404s — the binding input still accepts a typed key.
    retry: false,
  });

  const serverSchema: DraftSaveRequest = useMemo(() => {
    const data = draftQuery.data;
    if (!data) return EMPTY_SCHEMA;
    return {
      sections: data.sections,
      fields: data.fields,
      logic: data.logic ?? [],
      privacyNotice: data.privacyNotice ?? "",
      settings: data.settings ?? {},
    };
  }, [draftQuery.data]);

  const save = useCallback(
    async (schema: DraftSaveRequest) => {
      await apiFetch(`/forms/${formId}/draft`, { method: "PUT", body: JSON.stringify(schema) });
    },
    [formId],
  );

  const builder = useBuilderState(formId, serverSchema, save);
  const { schema, update } = builder;

  // Load the server copy once it arrives, unless a locally-mirrored draft survived a crash.
  useEffect(() => {
    if (!draftQuery.data) return;
    const local = readLocalDraft(formId);
    if (local) {
      setRecovered({ savedAt: local.savedAt });
      builder.resetTo(local.schema);
    } else {
      builder.resetTo(serverSchema);
    }
    // Deliberately keyed on the fetch completing, not on `builder`, which changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.data, formId]);

  // Memoised because `?? []` creates a fresh array each render, which would invalidate every
  // downstream memo and re-render the palette on every keystroke. Caught by react-hooks lint.
  const fieldTypes = useMemo(() => fieldTypesQuery.data?.data ?? [], [fieldTypesQuery.data]);
  const typeByName = useMemo(
    () => new Map(fieldTypes.map((entry) => [entry.type, entry])),
    [fieldTypes],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Keyboard sensor enabled — the builder must be operable without a mouse (Phase 4 prompt).
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addField = useCallback(
    (fieldType: FieldTypeSummary, sectionKey?: string | null) => {
      update((current) => {
        const existingKeys = new Set(current.fields.map((field) => field.key));
        const key = deriveKey(fieldType.label, existingKeys);
        const targetSection =
          sectionKey !== undefined ? sectionKey : (current.sections[0]?.key ?? null);

        const field: DraftField = {
          key,
          type: fieldType.type,
          label: fieldType.label,
          position: current.fields.length,
          rowGroup: 0,
          widthSpan: 12,
          isRequired: false,
          isReadOnly: false,
          visibility: "visible",
          isPii: false,
          isSensitive: false,
          isSearchable: fieldType.defaultSearchable,
          isDedupeKey: false,
          config: { ...fieldType.defaultConfig },
          validation: {},
          sectionKey: targetSection,
        };
        setSelectedKey(key);
        return { ...current, fields: [...current.fields, field] };
      });
    },
    [update],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeData = active.data.current as
        { source?: string; fieldType?: FieldTypeSummary } | undefined;

      // Dropping a palette item onto the canvas adds it.
      if (activeData?.source === "palette" && activeData.fieldType) {
        addField(activeData.fieldType);
        return;
      }

      if (active.id === over.id) return;

      update((current) => {
        const oldIndex = current.fields.findIndex((field) => field.key === active.id);
        const newIndex = current.fields.findIndex((field) => field.key === over.id);
        if (oldIndex < 0 || newIndex < 0) return current;

        const target = current.fields[newIndex];
        const moved = arrayMove(current.fields, oldIndex, newIndex).map((field, index) => ({
          ...field,
          position: index,
        }));

        // Dragging into another section reassigns the field to it.
        const activeField = moved.find((field) => field.key === active.id);
        if (activeField && target && activeField.sectionKey !== target.sectionKey) {
          activeField.sectionKey = target.sectionKey;
        }
        return { ...current, fields: moved };
      });
    },
    [addField, update],
  );

  const runValidation = useCallback(async (): Promise<DraftValidationIssue[]> => {
    const result = await apiPost<{ valid: boolean; issues: DraftValidationIssue[] }>(
      `/forms/${formId}/draft/validate`,
      {},
    );
    setIssues(result.issues);
    return result.issues;
  }, [formId]);

  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const openPreview = async (): Promise<void> => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      // Save first — the preview link points at what's stored, not the in-memory schema, so an
      // unsaved edit would otherwise silently preview stale content.
      await builder.saveNow();
      const { url } = await apiPost<{ url: string }>(`/forms/${formId}/preview-link`, {});
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setPreviewError("Could not create a preview link. Try again.");
    } finally {
      setPreviewing(false);
    }
  };

  const openPublish = async (): Promise<void> => {
    await builder.saveNow();
    const [found, diff] = await Promise.all([
      runValidation(),
      apiGet<PublishDiff>(`/forms/${formId}/publish/diff`),
    ]);
    setIssues(found);
    setPublishDiff(diff);
    setPublishOpen(true);
  };

  /**
   * Sends the publisher to wherever a blocking issue is actually fixed.
   *
   * Without this, "a privacy notice is required" is a dead end: the control lives on a tab the
   * publisher has probably never opened, and the message on its own gives no way to find it.
   */
  const goToIssue = (issue: DraftValidationIssue): void => {
    setPublishOpen(false);

    if (issue.code === "PRIVACY_NOTICE_REQUIRED") {
      setTab("settings");
      // After the tab has mounted — the control does not exist to focus until then.
      requestAnimationFrame(() => {
        const notice = document.getElementById("privacy-notice");
        notice?.scrollIntoView({ block: "center" });
        notice?.focus();
      });
      return;
    }

    if (issue.ruleId) {
      setTab("logic");
      return;
    }

    setTab("build");
    if (issue.fieldKey && schema.fields.some((field) => field.key === issue.fieldKey)) {
      setSelectedKey(issue.fieldKey);
    }
  };

  const doPublish = async (): Promise<void> => {
    setIsPublishing(true);
    try {
      await apiPost(`/forms/${formId}/publish`, {});
      setPublishOpen(false);
      await draftQuery.refetch();
    } catch {
      await runValidation();
    } finally {
      setIsPublishing(false);
    }
  };

  const selectedField = schema.fields.find((field) => field.key === selectedKey) ?? null;

  const updateField = (key: string, next: DraftField): void =>
    update((current) => ({
      ...current,
      fields: current.fields.map((field) => (field.key === key ? next : field)),
    }));

  if (draftQuery.isLoading || fieldTypesQuery.isLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading builder…</div>;
  }

  // Never render an editable builder over a draft that failed to load. Without this the schema
  // falls back to EMPTY_SCHEMA, the first edit marks it dirty, and autosave writes the empty
  // schema over the real draft — silent, irreversible data loss from a transient network blip.
  if (draftQuery.isError || fieldTypesQuery.isError) {
    return (
      <div className="p-8">
        <h2 className="text-lg font-semibold">This form could not be loaded</h2>
        <p className="mt-2 max-w-prose text-sm text-muted-foreground">
          Your existing questions are safe on the server — nothing has been changed. Editing is
          disabled until the form loads, so that an empty builder cannot overwrite your work.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 text-sm"
            onClick={() => {
              void draftQuery.refetch();
              void fieldTypesQuery.refetch();
            }}
          >
            Try again
          </button>
          <button type="button" className="rounded-md border px-3 py-1.5 text-sm" onClick={onExit}>
            Back to forms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <header className="flex items-center gap-3 border-b bg-card px-4 py-2">
        <button
          type="button"
          onClick={onExit}
          className="rounded border px-2 py-1 text-xs hover:bg-secondary"
        >
          ← Forms
        </button>

        <nav className="flex gap-1" aria-label="Builder sections">
          {(
            [
              "build",
              "logic",
              "settings",
              "design",
              "duplicates",
              "notifications",
              "webhooks",
            ] as Tab[]
          ).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setTab(option)}
              aria-current={tab === option ? "page" : undefined}
              className={`rounded px-3 py-1 text-xs capitalize ${
                tab === option ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >
              {option}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <span
            className={`text-xs ${builder.saveState === "error" ? "text-brand-red" : "text-muted-foreground"}`}
            role="status"
            aria-live="polite"
          >
            {SAVE_STATE_LABEL[builder.saveState]}
          </span>

          <button
            type="button"
            onClick={builder.undo}
            disabled={!builder.canUndo}
            className="rounded border px-2 py-1 text-xs disabled:opacity-40"
            aria-label="Undo"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={builder.redo}
            disabled={!builder.canRedo}
            className="rounded border px-2 py-1 text-xs disabled:opacity-40"
            aria-label="Redo"
          >
            Redo
          </button>
          <button
            type="button"
            onClick={() => void runValidation()}
            className="rounded border px-2 py-1 text-xs hover:bg-secondary"
          >
            Validate
          </button>
          <button
            type="button"
            onClick={() => void openPreview()}
            disabled={previewing}
            title="Opens the current draft in a new tab, exactly as an applicant would see it"
            className="rounded border px-2 py-1 text-xs hover:bg-secondary disabled:opacity-50"
          >
            {previewing ? "Preparing…" : "Preview"}
          </button>
          <button
            type="button"
            onClick={() => void openPublish()}
            className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary-hover"
          >
            Publish
          </button>
        </div>
      </header>

      {recovered && (
        <div className="flex items-center gap-3 border-b bg-accent/10 px-4 py-2 text-xs">
          <span>
            Recovered unsaved changes from {new Date(recovered.savedAt).toLocaleTimeString()}.
          </span>
          <button
            type="button"
            className="rounded border px-2 py-0.5 hover:bg-secondary"
            onClick={() => {
              clearLocalDraft(formId);
              builder.resetTo(serverSchema);
              setRecovered(null);
            }}
          >
            Discard and use the saved version
          </button>
          <button type="button" className="underline" onClick={() => setRecovered(null)}>
            Keep them
          </button>
        </div>
      )}

      {previewError && (
        <div className="border-b bg-brand-red/5 px-4 py-2 text-xs text-brand-red">
          {previewError}
        </div>
      )}

      {issues.length > 0 && tab === "build" && (
        <div className="border-b bg-brand-red/5 px-4 py-2">
          <p className="text-xs font-medium text-brand-red">
            {issues.length} validation problem{issues.length === 1 ? "" : "s"}
          </p>
          <ul className="mt-1 space-y-0.5">
            {issues.slice(0, 4).map((issue, index) => (
              <li key={index} className="text-xs text-brand-red">
                • {issue.message}
                <button
                  type="button"
                  onClick={() => goToIssue(issue)}
                  className="ml-1 whitespace-nowrap font-medium underline underline-offset-2"
                >
                  Take me there
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Panes ─────────────────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1">
        {tab === "build" && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <FieldPalette fieldTypes={fieldTypes} onAdd={(type) => addField(type)} />

            <div className="flex min-w-0 flex-1">
              <Canvas
                fields={schema.fields}
                sections={schema.sections}
                selectedKey={selectedKey}
                typeLabelFor={(type) => typeByName.get(type)?.label ?? type}
                onSelect={setSelectedKey}
                onFieldChange={updateField}
                onDuplicate={(key) =>
                  update((current) => {
                    const source = current.fields.find((field) => field.key === key);
                    if (!source) return current;
                    const existing = new Set(current.fields.map((field) => field.key));
                    const copy: DraftField = {
                      ...source,
                      key: deriveKey(`${source.label} copy`, existing),
                      position: current.fields.length,
                    };
                    return { ...current, fields: [...current.fields, copy] };
                  })
                }
                onDelete={(key) => {
                  update((current) => ({
                    ...current,
                    fields: current.fields.filter((field) => field.key !== key),
                    // A rule left pointing at a deleted field is a validation error; drop the
                    // dangling references here so the builder does not create one by accident.
                    logic: (current.logic as LogicRule[]).filter(
                      (rule) =>
                        !rule.conditions.some((condition) => condition.field === key) &&
                        !rule.effects.some(
                          (effect) => effect.targetType === "field" && effect.target === key,
                        ),
                    ),
                  }));
                  if (selectedKey === key) setSelectedKey(null);
                }}
                onAddSection={() =>
                  update((current) => {
                    const index = current.sections.length + 1;
                    const section: DraftSection = {
                      key: `section_${index}`,
                      title: `Section ${index}`,
                      pageNumber: current.sections[current.sections.length - 1]?.pageNumber ?? 1,
                      position: current.sections.length,
                      collapsible: false,
                    };
                    return { ...current, sections: [...current.sections, section] };
                  })
                }
                onSectionChange={(key, next) =>
                  update((current) => ({
                    ...current,
                    sections: current.sections.map((section) =>
                      section.key === key ? next : section,
                    ),
                  }))
                }
                onToggleSideBySide={(key) =>
                  update((current) => ({
                    ...current,
                    fields:
                      current.fields.find((field) => field.key === key)?.rowGroup !== 0
                        ? splitFromRow(current.fields, key)
                        : joinPreviousRow(current.fields, key),
                  }))
                }
              />

              {/* A fixed, screen-safe column. It used to grow to the simulated device width
                  (up to 1080px), which pushed palette + canvas + preview + properties past the
                  viewport on any normal laptop and left the panes overlapping. The preview scales
                  its frame to fit this column instead — see PreviewPane. */}
              <div className="w-[26rem] shrink-0 border-l 2xl:w-[34rem]">
                <PreviewPane
                  fields={schema.fields}
                  sections={schema.sections}
                  logic={schema.logic as LogicRule[]}
                  width={previewWidth}
                  onWidthChange={setPreviewWidth}
                />
              </div>
            </div>

            <PropertiesPanel
              field={selectedField}
              fieldType={selectedField ? typeByName.get(selectedField.type) : undefined}
              profileAttributes={profileAttributesQuery.data?.data ?? []}
              programmeAreaStates={formQuery.data?.programme?.areaStates ?? []}
              canJoinRow={
                selectedField ? canJoinPreviousRow(schema.fields, selectedField.key) : false
              }
              sharesRow={(selectedField?.rowGroup ?? 0) !== 0}
              onJoinRow={() =>
                selectedField &&
                update((current) => ({
                  ...current,
                  fields: joinPreviousRow(current.fields, selectedField.key),
                }))
              }
              onSplitRow={() =>
                selectedField &&
                update((current) => ({
                  ...current,
                  fields: splitFromRow(current.fields, selectedField.key),
                }))
              }
              onChange={(next) => selectedField && updateField(selectedField.key, next)}
            />
          </DndContext>
        )}

        {tab === "logic" && (
          <div className="flex-1 overflow-y-auto">
            <LogicTab
              logic={schema.logic as LogicRule[]}
              fields={schema.fields}
              sections={schema.sections}
              issues={issues}
              onChange={(next) => update((current) => ({ ...current, logic: next }))}
            />
          </div>
        )}

        {tab === "settings" && (
          <div className="flex-1 overflow-y-auto">
            <SettingsTab schema={schema} formId={formId} onChange={update} />
          </div>
        )}

        {tab === "design" && (
          <div className="flex-1 overflow-y-auto">
            <DesignTab schema={schema} onChange={update} />
          </div>
        )}

        {tab === "duplicates" && (
          <div className="flex-1 overflow-y-auto p-4">
            <DedupeRulesPanel
              formId={formId}
              // The draft's fields: keys are stable after publish (CLAUDE.md), so what the builder
              // holds and what a live submission carries are the same names.
              fields={schema.fields.map((field) => ({
                key: field.key,
                label: field.label,
                type: field.type,
              }))}
            />
          </div>
        )}

        {tab === "notifications" && (
          <div className="flex-1 overflow-y-auto p-4">
            <NotificationsPanel formId={formId} />
          </div>
        )}

        {tab === "webhooks" && (
          <div className="flex-1 overflow-y-auto p-4">
            <WebhooksPanel formId={formId} />
          </div>
        )}
      </div>

      {publishOpen && (
        <PublishModal
          diff={publishDiff}
          issues={issues}
          isPublishing={isPublishing}
          onPublish={() => void doPublish()}
          onClose={() => setPublishOpen(false)}
          onFixIssue={goToIssue}
        />
      )}
    </div>
  );
};
